/**
 * Validation Service for centralized field validation via backend API
 */

import axios, { AxiosError } from 'axios';
import { API_URL } from '../shared/services/api';
import { useAuthStore } from '../store/authStore';

// Types for validation requests and responses
export interface FieldValidationRequest {
  field_path: string;
  value: string;
  validation_type?: 'beo_regex' | 'format_spec' | 'custom' | 'auto';
  format_spec?: string;
  context?: Record<string, any>;
}

export interface BatchValidationRequest {
  fields: FieldValidationRequest[];
  stop_on_first_error?: boolean;
  validation_type?: 'beo_regex' | 'format_spec' | 'custom' | 'auto';
}

export interface FieldValidationResponse {
  field_path: string;
  value: string;
  is_valid: boolean;
  validation_type_used: string;
  error_message?: string;
  pattern_used?: string;
  format_hint?: string;
  examples?: string[];
  warning?: string;
}

export interface BatchValidationResponse {
  results: FieldValidationResponse[];
  summary: {
    total: number;
    validated: number;
    valid: number;
    invalid: number;
  };
  stopped_early: boolean;
}

export interface ValidationPatternInfo {
  field_path: string;
  pattern: string;
  format_spec?: string;
  description: string;
  examples: string[];
  source: string;
}

export interface GetPatternsResponse {
  patterns: ValidationPatternInfo[];
  total_count: number;
}

export interface RequiredFieldInfo {
  field_path: string;
  field_name: string;
  section: string;
  value: any;
  is_present: boolean;
}

export interface DocumentValidationRequest {
  extracted_data: any;
  module_name: string;
  check_required?: boolean;
  check_format?: boolean;
  check_business_rules?: boolean;
}

export interface DocumentValidationResponse {
  is_valid: boolean;
  completeness_percent: number;
  field_validations: FieldValidationResponse[];
  required_fields: RequiredFieldInfo[];
  missing_required: string[];
  warnings: string[];
  errors: string[];
  summary: {
    total_required: number;
    present_required: number;
    missing_required: number;
    total_validated: number;
    valid_fields: number;
    invalid_fields: number;
    warning_count: number;
    error_count: number;
  };
}

// Cache for validation results (TTL: 5 minutes)
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const validationCache = new Map<string, { result: FieldValidationResponse; timestamp: number }>();

// Debounce timers for real-time validation
const debounceTimers = new Map<string, NodeJS.Timeout>();

class ValidationService {
  private baseURL = `${API_URL}/api/v1/validation`;

  /**
   * Check if user is authenticated
   */
  private isAuthenticated(): boolean {
    const token = useAuthStore.getState().token;
    return !!token && !!token.access_token;
  }

  /**
   * Get authorization headers
   */
  private getHeaders(): Record<string, string> {
    const token = useAuthStore.getState().token;
    return token
      ? { Authorization: `Bearer ${token.access_token}` }
      : {};
  }

  /**
   * Validate a single field value
   * @param request Field validation request
   * @param useCache Whether to use cached results (default: true)
   * @returns Validation response
   */
  async validateField(
    request: FieldValidationRequest,
    useCache: boolean = true
  ): Promise<FieldValidationResponse> {
    // Check authentication first
    if (!this.isAuthenticated()) {
      console.debug('Skipping field validation - user not authenticated');
      return {
        field_path: request.field_path,
        value: request.value,
        is_valid: false,
        validation_type_used: 'none',
        error_message: 'Authentication required',
      };
    }

    // Create cache key
    const cacheKey = `${request.field_path}:${request.value}:${request.validation_type || 'auto'}`;

    // Check cache if enabled
    if (useCache) {
      const cached = validationCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        console.log(`Validation cache hit for ${request.field_path}`);
        return cached.result;
      }
    }

    try {
      const response = await axios.post<FieldValidationResponse>(
        `${this.baseURL}/validate-field`,
        request,
        { headers: this.getHeaders() }
      );

      // Cache the result
      validationCache.set(cacheKey, {
        result: response.data,
        timestamp: Date.now()
      });

      // Clean up old cache entries
      this.cleanupCache();

      return response.data;
    } catch (error) {
      // Only log non-401 errors as errors
      const axiosError = error as AxiosError;
      if (axiosError.response?.status !== 401) {
        console.error('Field validation error:', error);
      }
      throw this.handleError(axiosError);
    }
  }

  /**
   * Validate a single field with debouncing for real-time validation
   * @param request Field validation request
   * @param debounceMs Debounce delay in milliseconds (default: 300ms)
   * @returns Promise that resolves to validation response
   */
  async validateFieldDebounced(
    request: FieldValidationRequest,
    debounceMs: number = 300
  ): Promise<FieldValidationResponse> {
    const debounceKey = request.field_path;

    return new Promise((resolve, reject) => {
      // Clear existing timer for this field
      const existingTimer = debounceTimers.get(debounceKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Set new timer
      const timer = setTimeout(async () => {
        try {
          const result = await this.validateField(request);
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          debounceTimers.delete(debounceKey);
        }
      }, debounceMs);

      debounceTimers.set(debounceKey, timer);
    });
  }

  /**
   * Validate multiple fields in batch
   * @param request Batch validation request
   * @returns Batch validation response
   */
  async validateBatch(request: BatchValidationRequest): Promise<BatchValidationResponse> {
    try {
      const response = await axios.post<BatchValidationResponse>(
        `${this.baseURL}/validate-fields`,
        request,
        { headers: this.getHeaders() }
      );

      // Cache individual results
      response.data.results.forEach(result => {
        const cacheKey = `${result.field_path}:${result.value}:${result.validation_type_used}`;
        validationCache.set(cacheKey, {
          result,
          timestamp: Date.now()
        });
      });

      return response.data;
    } catch (error) {
      console.error('Batch validation error:', error);
      throw this.handleError(error as AxiosError);
    }
  }

  /**
   * Validate a complete declaration structure
   * @param declarationData Complete declaration data
   * @returns Batch validation response for all fields
   */
  async validateDeclaration(declarationData: any): Promise<BatchValidationResponse> {
    try {
      const response = await axios.post<BatchValidationResponse>(
        `${this.baseURL}/validate-declaration`,
        declarationData,
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error) {
      console.error('Declaration validation error:', error);
      throw this.handleError(error as AxiosError);
    }
  }

  /**
   * Get available validation patterns
   * @param fieldPathFilter Optional filter for field paths
   * @returns List of available patterns
   */
  async getPatterns(fieldPathFilter?: string): Promise<GetPatternsResponse> {
    try {
      const params = fieldPathFilter ? { field_path: fieldPathFilter } : {};
      const response = await axios.get<GetPatternsResponse>(
        `${this.baseURL}/patterns`,
        {
          headers: this.getHeaders(),
          params
        }
      );

      return response.data;
    } catch (error) {
      console.error('Get patterns error:', error);
      throw this.handleError(error as AxiosError);
    }
  }

  /**
   * Comprehensive document validation
   * @param extractedData Complete extracted data from document
   * @param moduleName Module name for validation context
   * @param options Optional validation options
   * @returns Complete document validation response
   */
  async validateDocument(
    extractedData: any,
    moduleName: string,
    options?: {
      checkRequired?: boolean;
      checkFormat?: boolean;
      checkBusinessRules?: boolean;
    }
  ): Promise<DocumentValidationResponse> {
    // Check authentication first to avoid unnecessary API calls
    if (!this.isAuthenticated()) {
      console.debug('Skipping document validation - user not authenticated');
      // Return empty validation response
      return {
        is_valid: false,
        completeness_percent: 0,
        field_validations: [],
        required_fields: [],
        missing_required: [],
        warnings: ['Authentication required'],
        errors: [],
        summary: {
          total_required: 0,
          present_required: 0,
          missing_required: 0,
          total_validated: 0,
          valid_fields: 0,
          invalid_fields: 0,
          warning_count: 1,
          error_count: 0,
        }
      };
    }

    try {
      const request: DocumentValidationRequest = {
        extracted_data: extractedData,
        module_name: moduleName,
        check_required: options?.checkRequired !== false,
        check_format: options?.checkFormat !== false,
        check_business_rules: options?.checkBusinessRules !== false
      };

      const response = await axios.post<DocumentValidationResponse>(
        `${this.baseURL}/validate-document`,
        request,
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error) {
      // Only log non-401 errors as errors
      const axiosError = error as AxiosError;
      if (axiosError.response?.status !== 401) {
        console.error('Document validation error:', error);
      }
      throw this.handleError(axiosError);
    }
  }

  /**
   * Clear validation cache
   */
  clearCache(): void {
    validationCache.clear();
    console.log('Validation cache cleared');
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupCache(): void {
    const now = Date.now();
    const entriesToDelete: string[] = [];

    validationCache.forEach((value, key) => {
      if (now - value.timestamp > CACHE_TTL) {
        entriesToDelete.push(key);
      }
    });

    entriesToDelete.forEach(key => validationCache.delete(key));

    if (entriesToDelete.length > 0) {
      console.log(`Cleaned up ${entriesToDelete.length} expired cache entries`);
    }
  }

  /**
   * Cancel all pending debounced validations
   */
  cancelPendingValidations(): void {
    debounceTimers.forEach(timer => clearTimeout(timer));
    debounceTimers.clear();
    console.log('All pending validations cancelled');
  }

  /**
   * Handle axios errors
   */
  private handleError(error: AxiosError): Error {
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data as any;

      // Handle 401 Unauthorized - don't log as error
      if (status === 401) {
        console.debug('Validation requires authentication - user not logged in');
        return new Error('Authentication required');
      }

      const message = data?.detail || data?.message || 'Validation failed';
      return new Error(message);
    } else if (error.request) {
      return new Error('No response from validation service');
    } else {
      return new Error('Validation request failed');
    }
  }

  /**
   * Validate a form field and return a formatted error message
   * Convenience method for form integration
   */
  async validateFormField(
    fieldPath: string,
    value: string,
    options?: {
      validationType?: 'beo_regex' | 'format_spec' | 'custom' | 'auto';
      formatSpec?: string;
      debounce?: boolean;
      debounceMs?: number;
    }
  ): Promise<{ isValid: boolean; error?: string; hint?: string; examples?: string[] }> {
    try {
      const request: FieldValidationRequest = {
        field_path: fieldPath,
        value,
        validation_type: options?.validationType,
        format_spec: options?.formatSpec
      };

      const response = options?.debounce
        ? await this.validateFieldDebounced(request, options.debounceMs)
        : await this.validateField(request);

      return {
        isValid: response.is_valid,
        error: response.error_message,
        hint: response.format_hint,
        examples: response.examples
      };
    } catch (error) {
      console.error(`Form field validation error for ${fieldPath}:`, error);
      return {
        isValid: false,
        error: error instanceof Error ? error.message : 'Validation failed'
      };
    }
  }

  /**
   * Validate multiple form fields at once
   * Convenience method for form submission
   */
  async validateFormFields(
    fields: Array<{ path: string; value: string }>
  ): Promise<{
    isValid: boolean;
    errors: Record<string, string>;
    summary: { valid: number; invalid: number; total: number };
  }> {
    try {
      const request: BatchValidationRequest = {
        fields: fields.map(f => ({
          field_path: f.path,
          value: f.value
        }))
      };

      const response = await this.validateBatch(request);

      const errors: Record<string, string> = {};
      response.results.forEach(result => {
        if (!result.is_valid && result.error_message) {
          errors[result.field_path] = result.error_message;
        }
      });

      return {
        isValid: response.summary.invalid === 0,
        errors,
        summary: {
          valid: response.summary.valid,
          invalid: response.summary.invalid,
          total: response.summary.total
        }
      };
    } catch (error) {
      console.error('Form fields validation error:', error);
      return {
        isValid: false,
        errors: { _error: error instanceof Error ? error.message : 'Validation failed' },
        summary: { valid: 0, invalid: fields.length, total: fields.length }
      };
    }
  }
}

// Export singleton instance
const validationService = new ValidationService();
export default validationService;

// Export class for testing
export { ValidationService };